/**
 * 当前用户今日打卡状态 — 侧栏常驻数据源。
 * 首次读取 + 统一资源通知/五分钟校验;
 * `refresh()` 供动作后/面板打开时强制重拉。后端是唯一真相源。
 *
 * sessions 流:今日 attendance 的 sessions 数组,供 useClockTicker 秒级重算。
 * 若 entry.attendanceId 非空则串行拉 getAttendanceById + load_relationships=['sessions']。
 * sessions 拉取失败时 console.error + entry 保留 + sessions 置 [](退化为静态快照)。
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAttendanceToday } from "@/api/userRealtime";
import { attendanceApi } from "@/api/api";
import type { AttendanceTodayEntry } from "@/types/userRealtime";
import type { AttendanceSessionRef } from "@/types/attendance";
import type { SnowflakeId } from "@/types/snowflake";

import { useRefreshSignal } from "@/features/realtime/useRefreshSignal";

export function useMyClockStatus(userId: SnowflakeId | null) {
  const [entry, setEntry] = useState<AttendanceTodayEntry | null>(null);
  const [sessions, setSessions] = useState<AttendanceSessionRef[]>([]);
  const [loaded, setLoaded] = useState(false);
  // 并发 refresh 序列守卫:轮询 tick / mount / 动作后 refreshAll 三个入口可并发,
  // 旧请求晚返回会把 UI 打回旧状态(最长持续到下个 60s tick)。只允许最新一次
  // 调用落 state,过期响应整体丢弃;entry+sessions 凑齐后一次性写入,消除
  // 「entry 已新、sessions 还旧」的瞬时撕裂。
  const refreshSeqRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!userId) return;
    const seq = ++refreshSeqRef.current;
    try {
      const todayEntry = await fetchAttendanceToday(userId, true);

      // 若有 attendanceId,串行拉 sessions 流供秒级计时器使用
      let nextSessions: AttendanceSessionRef[] = [];
      if (todayEntry?.attendanceId) {
        try {
          // axios 拦截器已解包 {code,data,msg} → 运行时即 AttendanceOutput;
          // api.ts 签名仍是 AxiosResponse,故此处显式断言解包后形状
          const resp = (await attendanceApi.getAttendanceById(
            String(todayEntry.attendanceId),
            { load_relationships: ["sessions"] },
          )) as unknown as { sessions?: AttendanceSessionRef[] };
          if (Array.isArray(resp?.sessions)) {
            nextSessions = resp.sessions;
          } else {
            console.warn("[useMyClockStatus] getAttendanceById: sessions 字段缺失,resp shape:", resp);
          }
        } catch (sessErr) {
          console.error("[useMyClockStatus] getAttendanceById sessions 拉取失败:", sessErr);
          // entry 照常落地,sessions 置 [] 使计时退化为静态快照
        }
      }

      if (seq !== refreshSeqRef.current) return; // 已有更新的 refresh,本次响应过期
      setEntry(todayEntry);
      setSessions(nextSessions);
    } catch (e) {
      // 只读轮询失败不打扰用户;dump 便于排查(可能是权限/网络)
      console.error("[useMyClockStatus] fetchAttendanceToday failed:", e);
      return false;
    } finally {
      if (seq === refreshSeqRef.current) setLoaded(true);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Invalidate all outstanding reads; this ref is a sequence counter.
    return () => { refreshSeqRef.current++; };
  }, [userId, refresh]);

  useRefreshSignal({ resource: "myAttendance", enabled: !!userId, onRefresh: refresh });
  return { entry, sessions, loaded, refresh };
}
